import { Link } from 'react-router';
import { List, useStyles, Caption, useTheme } from '@archipro-design/aria';
import { ArrowDown, ExternalLink } from 'lucide-react';
import * as S from './DetailsTab.style';
import type Product from '../../type/product';
import { getProductRangeUrl } from '~/modules/product/util/get-product-range-url';
import { getProductCategoryUrl } from '~/modules/product/util/get-product-category-url';

/** Anchor ID for the product range section, used for smooth scrolling. */
const PRODUCT_RANGE_SECTION_ID = 'product-range-section';

/**
 * Scrolls smoothly to the product range section on the page.
 * Falls back to standard link navigation if the section is not found.
 */
const SCROLL_OFFSET_PX = 50;

const scrollToRangeSection = (e: React.MouseEvent<HTMLAnchorElement>) => {
    const section = document.getElementById(PRODUCT_RANGE_SECTION_ID);
    if (section) {
        e.preventDefault();
        const top =
            section.getBoundingClientRect().top +
            window.scrollY -
            SCROLL_OFFSET_PX;
        window.scrollTo({ top, behavior: 'smooth' });
    }
};

interface DetailsTabProps {
    data: Product;
}

const hasFacetOptionContent = (data: Product) =>
    data.FacetOptions.some(
        (option) =>
            option.Approved &&
            !option.Facet.IsHidden &&
            !option.Facet.IsDetailPageDisabled,
    );

export const hasDetailsTabContent = (data: Product) => {
    const productInfoList = [
        data.Categories?.[0]?.PrettyTitle,
        data.LibraryRanges.some((range) => range.Title),
        data.Brand,
        data.Designer,
        data.Year,
        data.WarrantyDuration,
    ];

    const hasProductInfo = productInfoList.some(Boolean);

    return hasProductInfo || hasFacetOptionContent(data);
};

const DetailsTab = ({ data }: DetailsTabProps) => {
    const { css } = useStyles();
    const theme = useTheme();
    const rangeLinks = data.Professional
        ? data.LibraryRanges.filter((range) => range.Title).map((range) => ({
              key: range.ID,
              value: range.Title,
              link: getProductRangeUrl(data.Professional, range.Title),
          }))
        : [];

    // The in-page section lists other products across all of the product's
    // ranges, so it only stands in for a range's listing when there is one.
    const hasOtherProductsInRange =
        data.ProductRange.pageInfo.totalCount > 0 && rangeLinks.length === 1;

    const category = data.Categories?.[0];
    const categoryLink =
        category && data.Professional
            ? getProductCategoryUrl(data.Professional, category)
            : null;

    type InfoEntry = {
        key?: number;
        value?: string | null;
        link?: string | null;
    };
    const productInfoList: { label: string; entries: InfoEntry[] }[] = [
        {
            label: 'Category',
            entries: [{ value: category?.PrettyTitle, link: categoryLink }],
        },
        {
            label: rangeLinks.length > 1 ? 'Ranges' : 'Range',
            entries: rangeLinks,
        },
        { label: 'Brand', entries: [{ value: data.Brand }] },
        { label: 'Designer', entries: [{ value: data.Designer }] },
        { label: 'Year', entries: [{ value: data.Year }] },
        { label: 'Warranty', entries: [{ value: data.WarrantyDuration }] },
    ];

    const filteredProductInfoList = productInfoList
        .map((row) => ({
            ...row,
            entries: row.entries.filter((entry) => !!entry.value),
        }))
        .filter((row) => row.entries.length > 0)
        .map(({ label, entries }) => {
            const isRangeRow = label === 'Range' || label === 'Ranges';
            const isCategoryRow = label === 'Category';

            const isSoleEntry = entries.length === 1;
            const renderEntry = ({ key, value, link }: InfoEntry) => {
                const isRangeLink = isRangeRow && link;
                const isCategoryLink = isCategoryRow && link;
                const isRangeSectionLink =
                    isRangeLink && hasOtherProductsInRange;
                const isRangeExternalLink =
                    isRangeLink && !hasOtherProductsInRange;
                const isStyledLink =
                    isRangeSectionLink || isRangeExternalLink || isCategoryLink;

                if (!link) {
                    return <span key={key ?? value}>{value}</span>;
                }

                return (
                    <Link
                        key={key ?? value}
                        to={link}
                        onClick={
                            isRangeSectionLink
                                ? scrollToRangeSection
                                : undefined
                        }
                        target={
                            isCategoryLink || isRangeExternalLink
                                ? '_blank'
                                : undefined
                        }
                        rel={
                            isCategoryLink || isRangeExternalLink
                                ? 'noopener noreferrer'
                                : undefined
                        }
                        className={
                            isStyledLink
                                ? 'group/detail-link inline-flex items-center' +
                                  (isSoleEntry ? ' w-full' : '') +
                                  ' justify-end gap-1 md:justify-start' +
                                  ' cursor-pointer text-gray-500' +
                                  ' transition-colors hover:text-gray-700'
                                : 'text-current underline decoration-current' +
                                  ' underline-offset-2 hover:opacity-80'
                        }
                    >
                        <span
                            className={
                                isStyledLink
                                    ? 'underline decoration-gray-300' +
                                      ' underline-offset-2' +
                                      ' group-hover/detail-link:decoration-gray-500'
                                    : undefined
                            }
                        >
                            {value}
                        </span>
                        {isRangeSectionLink && (
                            <ArrowDown
                                className="size-3 shrink-0 opacity-30"
                                strokeWidth={2}
                            />
                        )}
                        {(isCategoryLink || isRangeExternalLink) && (
                            <ExternalLink
                                className="size-3 shrink-0 opacity-30"
                                strokeWidth={2}
                            />
                        )}
                    </Link>
                );
            };

            const [soleEntry] = entries;
            const valueContent =
                soleEntry && entries.length === 1 ? (
                    renderEntry(soleEntry)
                ) : (
                    <span className="inline-flex flex-wrap justify-end gap-x-1 md:justify-start">
                        {entries.map((entry, index) => (
                            <span
                                key={entry.key ?? entry.value}
                                className="inline-flex items-center"
                            >
                                {renderEntry(entry)}
                                {index < entries.length - 1 && ','}
                            </span>
                        ))}
                    </span>
                );

            return {
                label: {
                    children: label,
                    styles: {
                        fontSize: 'unset',
                    },
                    className: `${css(
                        S.ProductDetailsListItemLabel,
                    )} product-details-list-item-label`,
                },
                customChildComponent: (
                    <div
                        className={`${css(
                            S.ProductDetailsListItemValue,
                        )} product-details-list-item-value text-base`}
                    >
                        {valueContent}
                    </div>
                ),
            };
        });

    const facetOptions = data.FacetOptions.filter(
        (option) =>
            option.Approved &&
            !option.Facet.IsHidden &&
            !option.Facet.IsDetailPageDisabled,
    )
        .reduce<
            {
                ID: number;
                Label: string;
                IsGeneral: boolean;
                options: string[];
            }[]
        >((acc, curr) => {
            const {
                Facet: { ID, Label, IsGeneral },
                Name,
            } = curr;
            const facet = acc.find((facet) => facet.ID === ID);
            if (facet) {
                facet.options.push(Name);
            } else {
                acc.push({
                    ID: ID,
                    Label,
                    IsGeneral,
                    options: [Name],
                });
            }
            return acc;
        }, [])
        .sort((a, b) => a.ID - b.ID);

    if (filteredProductInfoList.length === 0 && facetOptions.length === 0) {
        return null;
    }

    const categoryFacetAttrsList = facetOptions
        .filter(({ IsGeneral }) => !IsGeneral)
        .map(({ Label: label, options }) => ({
            label: {
                children: label,
                className: `${css(
                    S.ProductDetailsListItemLabel,
                )} product-details-list-item-label`,
            },
            customChildComponent:
                options.length === 1 ? (
                    <div
                        className={`${css(
                            S.ProductDetailsListItemValue,
                        )} product-details-list-item-value text-base`}
                    >
                        {options[0]}
                    </div>
                ) : (
                    <ul className={css(S.AttributeOptions)}>
                        {options.map((option) => (
                            <li
                                className={`${css(
                                    S.ProductDetailsListItemValue,
                                )} product-details-list-item-value text-base`}
                                key={option}
                            >
                                {option}
                            </li>
                        ))}
                    </ul>
                ),
        }));

    const generalFacetAttrsList = facetOptions
        .filter(({ IsGeneral }) => IsGeneral)
        .map(({ Label: label, options }) => ({
            label: {
                children: label,
                className: `${css(
                    S.ProductDetailsListItemLabel,
                )} product-details-list-item-label`,
            },
            customChildComponent:
                options.length === 1 ? (
                    <div
                        className={`${css(
                            S.ProductDetailsListItemValue,
                        )} product-details-list-item-value text-base`}
                    >
                        {options[0]}
                    </div>
                ) : (
                    <ul className={css(S.AttributeOptions)}>
                        {options.map((option) => (
                            <li
                                className={`${css(
                                    S.ProductDetailsListItemValue,
                                )} product-details-list-item-value text-base`}
                                key={option}
                            >
                                {option}
                            </li>
                        ))}
                    </ul>
                ),
        }));

    return (
        <div>
            {filteredProductInfoList.length > 0 && (
                <div className={css(S.ProductDetailsContainer)}>
                    <List
                        items={filteredProductInfoList}
                        className={css(S.ProductDetailsList)}
                        variables={{
                            listBorderColor:
                                theme.siteVariables.colors.grey[125],
                        }}
                    />
                </div>
            )}

            {categoryFacetAttrsList.length > 0 && (
                <div className={css(S.AttributeContainer)}>
                    <Caption className={css(S.AttributeCaption)} variant="03">
                        Attributes
                    </Caption>
                    <List
                        items={categoryFacetAttrsList}
                        className={css(S.ProductDetailsList)}
                        variables={{
                            listBorderColor:
                                theme.siteVariables.colors.grey[125],
                        }}
                    />
                </div>
            )}
            {generalFacetAttrsList.length > 0 && (
                <div className={css(S.AttributeContainer)}>
                    <Caption className={css(S.AttributeCaption)} variant="03">
                        General
                    </Caption>
                    <List
                        items={generalFacetAttrsList}
                        className={css(S.ProductDetailsList)}
                        variables={{
                            listBorderColor:
                                theme.siteVariables.colors.grey[125],
                        }}
                    />
                </div>
            )}
        </div>
    );
};

export default DetailsTab;
